import { WorkSearch } from "@models/work";
import { buildSpatieSort } from "../shared/utils/buildSpatieSort";
import { normalizeCustomDataFilter } from "../shared/utils/normalizeCustomDataFilter";

export type WorkInclude = "customData";

export type WorkSortField =
  | "id"
  | "work_code"
  | "name"
  | "price"
  | "hours"
  | "manufacturer"
  | "model"
  | "created_at"
  | "updated_at";

export type WorkSort = WorkSortField | `-${WorkSortField}`;

const WORK_SORT_FIELDS: WorkSortField[] = [
  "id",
  "work_code",
  "name",
  "price",
  "hours",
  "manufacturer",
  "model",
  "created_at",
  "updated_at",
];

export const WORK_BROWSE_INCLUDES: WorkInclude[] = ["customData"];
export const WORK_BROWSE_PER_PAGE = 10;

export interface WorkSearchParams {
  filter?: Partial<WorkSearch>;
  include?: WorkInclude[];
  sort?: WorkSort[];
  per_page?: number;
  cursor?: string;
  useCache?: boolean;
}

/**
 * Strips empty custom_data entries and normalizes custom_data filter shape for work search.
 * @param filter WorkSearch - Raw work search filter from browse UI.
 * @returns WorkSearch - Filter safe to send to the works search endpoint.
 */
export function normalizeWorkSearchFilter(filter: WorkSearch): WorkSearch {

  // Map custom_data array into API-expected search filter shape
  const custom_data = normalizeCustomDataFilter(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;

  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

/**
 * Builds the POST body for the works Spatie browse/search endpoint.
 * @param params WorkSearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - Request body fields for works/search.
 */
export function getWorkSearchBody(params: WorkSearchParams) {

  const { filter: rawFilter, include, sort, per_page = WORK_BROWSE_PER_PAGE, cursor, useCache } = params;

  // Normalize filter fields (e.g. custom data) for Spatie search API
  const filter = rawFilter ? normalizeWorkSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
    ...(useCache && { use_cache: true }),
  };
}

/**
 * Builds a Spatie sort string for work browse from column key and order.
 * @param sortKey string - Column field name from the browse table.
 * @param order "ASC" | "DESC" - Sort direction from the browse table.
 * @returns WorkSort - Spatie sort token accepted by the works search API.
 */
export function buildWorkSort(sortKey: string, order: "ASC" | "DESC"): WorkSort {

  return buildSpatieSort(sortKey, order, WORK_SORT_FIELDS, "id") as WorkSort;
}
